import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import TetrisLoader from './TetrisLoader';
import TypedText from './TypedText';

// There's no real byte-level load metric to report, so progress is simulated on a fixed
// timeline up to 92% (always reads as "still working," never stalls dead). Enforced to run
// for at least MIN_VISIBLE_MS so the loader squares' disappear-in-sequence animation actually
// plays out instead of skipping straight to the end when the real page data loads almost
// instantly. Snaps to 100% once both the real data has arrived and that minimum has elapsed,
// then holds briefly so the completed fill is visible before the splash unmounts.
const MIN_VISIBLE_MS = 2400;

const SplashScreen = ({ loaded, onFinished }) => {
  const [progress, setProgress] = useState(0);
  const finishedRef = useRef(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startRef.current;

      if (loaded && elapsed >= MIN_VISIBLE_MS) {
        setProgress(100);
        clearInterval(interval);
        return;
      }

      const timeProgress = Math.min((elapsed / MIN_VISIBLE_MS) * 92, 92);
      setProgress((prev) => Math.max(prev, timeProgress));
    }, 100);

    return () => clearInterval(interval);
  }, [loaded]);

  useEffect(() => {
    if (progress < 100 || finishedRef.current) return;
    finishedRef.current = true;
    const timeout = setTimeout(onFinished, 500);
    return () => clearTimeout(timeout);
  }, [progress, onFinished]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1d1d1d',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TetrisLoader size={80} progress={progress} />
        <TypedText />
      </Box>
    </Box>
  );
};

export default SplashScreen;
